import { parseQueryParams } from 'pricing-pages-ui-components/utils/queryParamUtils';
import { useEffect, useRef } from 'react';
import { useLocation } from 'quartz-routing/react-router';
import { useEligibleInAppTreatments } from '../../context/ExperimentContext';
import { useSetQueryParams } from '../../v2/utils/urls/appQueryParamUtils';

/**
 * Mirrors registered in-app eligible treatments onto the page URL as
 * `<treatmentKey>=<group>` query params (e.g. `?ppfe-0003=variation`).
 *
 * Why: downstream surfaces — the buy interstitial, checkout, and Amplitude
 * events derived from URL — rely on the cohort being on the URL of the page
 * the user clicked from. The interstitial flow forwards `getQueryParams()`
 * automatically, so once the cohort is on the pricing page URL it survives
 * through `/pricing-interstitial/...` for free.
 *
 * Cohorts are sticky for the SPA session under the intent-to-treat
 * paradigm — see arbor docs/experiments.mdx. The clear-on-removal path
 * below exists for the rare case where a caller explicitly deregisters
 * a treatment (e.g. `useChatLoader` flipping eligibility per route);
 * tracking via a ref means we only clear keys this hook itself wrote.
 */
export const useSyncInAppTreatmentsToUrl = () => {
  const treatments = useEligibleInAppTreatments();
  const setQueryParams = useSetQueryParams();
  const location = useLocation();
  const previouslyWrittenKeysRef = useRef(new Set());
  useEffect(() => {
    const currentParams = parseQueryParams(location.search);
    const currentKeys = new Set(treatments.map(t => t.treatmentKey));
    const previousKeys = previouslyWrittenKeysRef.current;
    const updates = {};
    for (const treatment of treatments) {
      if (currentParams[treatment.treatmentKey] !== treatment.group) {
        updates[treatment.treatmentKey] = treatment.group;
      }
    }
    for (const key of previousKeys) {
      if (!currentKeys.has(key) && currentParams[key] !== undefined) {
        updates[key] = '';
      }
    }
    if (Object.keys(updates).length > 0) {
      setQueryParams(updates);
    }
    previouslyWrittenKeysRef.current = currentKeys;
  }, [treatments, location.search, setQueryParams]);
};